<template>
  <div class="dash-root">
    <div class="m-hd">
      <div class="h-side">
        <span class="h-clock">{{ clock }}<small>{{ today }}</small></span>
      </div>
      <div class="h-title"><i class="l"></i><b>{{ sysConfig.dashboard_name || '粮库资产可视化平台' }}</b><i class="r"></i></div>
      <div class="h-side h-right">
        <span class="h-btn on">{{ selectedStore ? '粮库详情' : '集团总览' }}</span>
        <span class="h-btn back" @click="goBack">返回管理平台</span>
      </div>
    </div>

    <div class="m-body" v-if="overview">
      <!-- 左列：固定宽度 -->
      <div class="col side">
        <!-- 全局：资产总览 -->
        <div class="p overview" v-if="!selectedStore">
          <div class="p-hd">资产总览<span class="meta">集团全域</span></div>
          <div class="p-bd">
            <div class="kpis kpis-4">
              <div class="kpi-card">
                <div class="kpi-v">{{ fmt(summary.storeTotal) }}<span class="u">座</span></div>
                <div class="kpi-l">下辖粮库</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-v">{{ fmt(summary.warehouseTotal) }}<span class="u">栋</span></div>
                <div class="kpi-l">仓房总数</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-v area">{{ fmtOneDecimal(summary.storeAreaMu) }}<span class="u">亩</span></div>
                <div class="kpi-l">粮库总面积</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-v cam">{{ fmt(summary.cameraTotal) }}<span class="u">路</span></div>
                <div class="kpi-l">摄像机总数</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 选中：粮库 KPI 卡片 -->
        <div class="p store-kpi" v-else>
          <div class="p-hd">粮库总览<span class="meta">{{ selectedStore.street || '' }}</span></div>
          <div class="p-bd">
            <div class="sk-head">
              <span class="sk-name">{{ selectedStore.name }}</span>
              <span class="tag" :class="'st' + selectedStore.status">{{ statusText(selectedStore.status) }}</span>
            </div>
            <div class="sk-kpis">
              <div class="sk-card sk-c1"><b>{{ storeWarehouses.length }}</b><span>仓房</span></div>
              <div class="sk-card sk-c2"><b>{{ totalCapacity }}</b><span>容量(t)</span></div>
              <div class="sk-card sk-c3"><b>{{ usedCapacity }}</b><span>已用(t)</span></div>
              <div class="sk-card sk-c4"><b>{{ usedRate }}%</b><span>利用率</span></div>
            </div>
            <div class="sk-kpis">
              <div class="sk-card sk-c5"><b>{{ storeCameras.length }}</b><span>监控</span></div>
              <div class="sk-card sk-c6"><b>{{ storeOnline }}</b><span>在线</span></div>
              <div class="sk-card sk-c7"><b>{{ pendingStoreAlarms }}</b><span>待处理报警</span></div>
              <div class="sk-card sk-c8"><b>{{ pendingStoreWorkOrders }}</b><span>待处理工单</span></div>
            </div>
            <div class="sk-detail">
              <div><span>面积</span><b>{{ fmtOneDecimal(selectedStore.area) }} 亩</b></div>
              <div><span>负责人</span><b>{{ selectedStore.manager || '—' }}</b></div>
              <div><span>电话</span><b>{{ selectedStore.phone || '—' }}</b></div>
              <div class="sk-addr"><span>地址</span><b>{{ selectedStore.address || '—' }}</b></div>
            </div>
          </div>
        </div>

        <!-- 全局：粮库片区分布 -->
        <div class="p area" v-if="!selectedStore">
          <div class="p-hd">粮库片区分布<span class="meta">按街道</span></div>
          <div class="p-bd">
            <div ref="areaChartEl" class="area-chart"></div>
            <div v-if="!storeByArea.length" class="empty-state">
              <i class="empty-icon">!</i>
              <div class="empty-title">暂无粮库数据</div>
            </div>
          </div>
        </div>

        <!-- 选中：该粮库仓房列表 / 租赁合同列表 -->
        <div class="p grow" v-else>
          <template v-if="selectedStore.status === 2">
            <div class="p-hd">租赁合同列表<span class="meta">{{ storeContracts.length }} 份</span></div>
            <div class="p-bd" style="overflow:auto">
              <div class="lease-item" v-for="c in storeContracts" :key="c.id" :class="c.status" @click="openContract(c)">
                <div class="lease-h"><span class="lease-name">{{ c.name }}</span><span class="lease-store">{{ c.statusText }}</span></div>
                <div class="lease-m"><span>{{ c.endDate }}</span><span class="lease-days" :class="c.status">{{ leaseDaysText(c) }}</span></div>
              </div>
              <div v-if="!storeContracts.length" class="empty-state">
                <i class="empty-icon">!</i>
                <div class="empty-title">该粮库暂无租赁合同</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="p-hd">仓房利用率<span class="meta">{{ storeWarehouses.length }} 栋</span></div>
            <div class="p-bd wh-wrap">
              <div class="wh-bar" v-for="w in storeWarehouses" :key="w.id">
                <div class="wh-bar-h">
                  <span class="wh-n">{{ w.name }}</span>
                  <span class="wh-s" :class="whState(w)">{{ whStateText(w) }}</span>
                  <span class="wh-pct" :class="whPctClass(w)">{{ whPct(w) }}%</span>
                </div>
                <div class="wh-bar-track">
                  <div class="wh-bar-fill" :class="whPctClass(w)" :style="{ width: whPct(w) + '%' }"></div>
                </div>
                <div class="wh-bar-sub">
                  <span>容量 {{ fmt(w.capacity) }}t</span>
                  <span>已用 {{ fmt(w.usedCapacity) }}t</span>
                  <span>面积 {{ fmt(w.area) }}㎡</span>
                </div>
              </div>
              <div v-if="!storeWarehouses.length" class="empty-state">
                <i class="empty-icon">!</i>
                <div class="empty-title">该粮库暂无仓房</div>
              </div>
            </div>
          </template>
        </div>

        <!-- 全局：合同执行情况 -->
        <div class="p contract" v-if="!selectedStore">
          <div class="p-hd">合同执行情况<span class="meta">{{ lease.total }} 份合同</span></div>
          <div class="p-bd">
            <div class="contract-tabs">
              <div class="ct-item" :class="{ active: activeLeaseTab === 'terminated', term: activeLeaseTab === 'terminated' }" @click="activeLeaseTab = 'terminated'">
                <b>{{ lease.terminated }}</b><span>已终止</span>
              </div>
              <div class="ct-item" :class="{ active: activeLeaseTab === 'expired' }" @click="activeLeaseTab = 'expired'">
                <b>{{ lease.expired }}</b><span>已到期</span>
              </div>
              <div class="ct-item" :class="{ active: activeLeaseTab === 'soon' }" @click="activeLeaseTab = 'soon'">
                <b>{{ lease.soon }}</b><span>30天内到期</span>
              </div>
              <div class="ct-item" :class="{ active: activeLeaseTab === 'normal' }" @click="activeLeaseTab = 'normal'">
                <b>{{ lease.normal }}</b><span>正常</span>
              </div>
            </div>
            <div class="lease-list">
              <div class="lease-item" v-for="c in filteredLeaseList" :key="c.id" :class="c.status" @click="openContract(c)">
                <div class="lease-h"><span class="lease-name">{{ c.name }}</span><span class="lease-store">{{ c.grainStoreName }}</span></div>
                <div class="lease-m"><span>{{ c.endDate }}</span><span class="lease-days" :class="c.status">{{ leaseDaysText(c) }}</span></div>
              </div>
              <div v-if="!filteredLeaseList.length" class="empty-state">
                <i class="empty-icon">!</i>
                <div class="empty-title">暂无{{ leaseTabText() }}合同</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 选中：巡检情况 -->
        <div class="p" v-else>
          <div class="p-hd">巡检情况<span class="meta">{{ patrolTotal }} 次</span></div>
          <div class="p-bd patrol-bd">
            <!-- 完成率圆环 + 状态分条 -->
            <div class="pr-top">
              <div class="pr-ring">
                <svg viewBox="0 0 36 36" class="pr-ring-svg">
                  <circle cx="18" cy="18" r="15.9" class="pr-ring-bg"/>
                  <circle cx="18" cy="18" r="15.9" class="pr-ring-fg"
                          :stroke-dasharray="`${patrolRate}, 100`"/>
                </svg>
                <div class="pr-ring-text">
                  <b>{{ patrolRate }}%</b>
                  <span>完成率</span>
                </div>
              </div>
              <div class="pr-stats">
                <div class="pr-stat pr-done"><b>{{ patrolDone }}</b><span>已完成</span></div>
                <div class="pr-stat pr-doing"><b>{{ patrolDoing }}</b><span>巡检中</span></div>
                <div class="pr-stat pr-pend"><b>{{ patrolPending }}</b><span>待巡检</span></div>
                <div class="pr-stat pr-expired"><b>{{ patrolExpired }}</b><span>已过期</span></div>
              </div>
            </div>
            <!-- 近7天巡检完成小条 -->
            <div class="pr-bars" v-if="patrolTotal">
              <div class="pr-bars-title">近7天巡检完成（单位：次）</div>
              <div class="pr-bars-row">
                <div class="pr-bar-item" v-for="(item, i) in countPatrolDoneByDay()" :key="i">
                  <span class="pr-bar-num" v-if="item.value">{{ item.value }}</span>
                  <span class="pr-bar-num pr-bar-zero" v-else>0</span>
                  <div class="pr-bar-v" :style="{ height: Math.max(4, item.value * 18) + 'px' }"></div>
                  <span class="pr-bar-l" :class="{ 'pr-bar-today': item.isToday }">{{ item.label }}</span>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <i class="empty-icon">!</i>
              <div class="empty-title">暂无巡检记录</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 中列：撑满 -->
      <div class="col center">
        <div class="center-top">
          <div class="ct-title">
            <i class="dot"></i>{{ selectedStore ? '粮库平面图 · ' + selectedStore.name : '粮库分布地图' }}
          </div>
          <el-button v-if="selectedStore" size="small" class="back-btn" @click="deselect">← 返回分布地图</el-button>
        </div>

        <div class="center-main">
          <!-- 高德地图：显示粮库点位 -->
          <div v-show="!selectedStore" class="amap-box" ref="amapEl">
            <div v-if="mapError" class="map-error">地图加载失败：请确认已配置有效的高德 Web 端 key（key 见 MapPicker.vue）。</div>
          </div>
          <!-- 状态筛选 -->
          <div v-if="!selectedStore" class="status-filter" @click.stop>
            <div class="sf-btns">
              <div v-for="s in statusOptions" :key="s.v" class="sf-btn" :class="{ active: activeStatuses.includes(s.v) }" @click="toggleStatus(s.v)">
                <i :style="{ background: s.color, boxShadow: '0 0 8px ' + s.color }"></i><span>{{ s.label }}</span><b>{{ s.count }}</b>
              </div>
            </div>
          </div>
          <!-- 平面图 + 摄像机点位 -->
          <div v-show="selectedStore" class="plan-wrap">
            <template v-if="selectedStore && selectedStore.planImage">
              <div class="plan-box" :style="{ aspectRatio: planAspect || '16 / 10' }">
                <img :src="selectedStore.planImage" class="plan-img" alt="" />
                <div v-for="cam in planCameras" :key="cam.id"
                     class="cam-marker"
                     :style="{ left: cam.posX + '%', top: cam.posY + '%' }"
                     @click="openVideo(cam)">
                  <CameraIcon :type="cam.cameraType === 'bullet' ? 'bullet' : 'dome'" :status="cam.online ? 1 : 0" :direction="cam.angle || 0" show-fov :size="40" />
                  <span class="cam-tip">{{ cam.name }}</span>
                </div>
              </div>
            </template>
            <div v-else class="empty-state empty-lg">
              <i class="empty-icon">!</i>
              <div class="empty-title">该粮库未上传平面图</div>
              <div class="empty-desc">无法显示摄像机点位，请到「粮库管理」编辑并上传平面图</div>
            </div>
          </div>

          <!-- 视频弹窗 — 覆盖 center-main -->
          <transition name="panel-fade">
            <div v-if="videoVisible" class="video-overlay" @click.self="videoVisible = false">
              <div class="video-overlay-inner">
                <div class="video-overlay-title">
                  <span>{{ videoCam ? (videoCam.name + ' · 监控视频') : '监控视频' }}</span>
                  <el-button text circle class="close-btn" @click="videoVisible = false">
                    <el-icon :size="20"><Close /></el-icon>
                  </el-button>
                </div>
                <div class="video-box big">
                  <img v-if="videoCam && isImage(videoCam.streamUrl)" :src="videoCam.streamUrl" class="v-img" alt="" />
                  <video v-else-if="videoCam && videoCam.streamUrl" :src="videoCam.streamUrl" controls autoplay class="v-vid"></video>
                  <div v-else class="empty-state">
                    <i class="empty-icon">!</i>
                    <div class="empty-title">该摄像机暂无视频流 / 图片</div>
                  </div>
                </div>
                <div class="video-meta" v-if="videoCam">
                  <span class="vm-tag" :class="videoCam.online ? 'on' : 'off'">{{ videoCam.online ? '在线' : '离线' }}</span>
                  <span>{{ videoCam.type }}</span>
                  <span v-if="videoCam.grainStoreName" class="vm-store">{{ videoCam.grainStoreName }}</span>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- 中间底部：全局5项统计 / 选中粮库7天趋势 -->
        <div class="stats-wrap" v-if="!selectedStore || hasTrendData">
          <div class="stats" v-if="!selectedStore">
            <div v-for="s in centerStatsArr" :key="s.l">
              <div class="v" :class="s.cls">{{ fmt(s.v) }}</div><div class="l">{{ s.l }}</div>
            </div>
          </div>
          <div class="trend-wrap" v-else>
            <div class="trend-title">近7天工单 / 报警趋势</div>
            <div ref="trendChartEl" class="trend-chart"></div>
          </div>
        </div>
      </div>

      <!-- 右列：固定宽度 -->
      <div class="col side">
        <div class="p">
          <div class="p-hd">天气情况<span class="meta">实时 · {{ weatherSource }}</span></div>
          <div class="p-bd">
            <div class="wx">
              <div><span v-html="iconTemp"></span><span class="t"><span class="l">温度</span><span class="v">{{ weather.temp }}<span class="u">°C</span></span></span></div>
              <div><span v-html="iconDrop"></span><span class="t"><span class="l">湿度</span><span class="v">{{ weather.humidity }}<span class="u">%</span></span></span></div>
              <div><span v-html="iconRain"></span><span class="t"><span class="l">雨晴</span><span class="v">{{ weather.rainText }}</span><span class="s" v-if="weather.precip>0">{{ weather.precip }}mm</span></span></div>
              <div><span v-html="iconWind"></span><span class="t"><span class="l">风向</span><span class="v" style="font-size:19px">{{ weather.wind }}</span></span></div>
            </div>
          </div>
        </div>

        <div class="p grow">
          <div class="p-hd">{{ selectedStore ? '该粮库未处理工单' : '未处理工单' }}<span class="meta">{{ displayWorkOrders.length }} 条</span></div>
          <div class="p-bd">
            <div class="wo-list">
              <div class="wo-item" v-for="w in displayWorkOrders" :key="w.id" @click="openWorkOrder(w)">
                <div class="wo-h">
                  <span class="wo-no">{{ w.orderNo || w.id }}</span>
                  <span class="wo-pri" :class="'p' + w.priority">{{ w.priorityText }}</span>
                  <span class="wo-st" :class="'s' + (w.status == null ? 0 : w.status)">{{ w.statusText }}</span>
                </div>
                <div class="wo-title">{{ w.title }}</div>
                <div class="wo-m"><span>{{ w.grainStoreName }}</span><span>{{ w.reporter || '—' }}</span><span>{{ w.createTime }}</span></div>
              </div>
              <div v-if="!displayWorkOrders.length" class="empty-state">
                <i class="empty-icon">!</i>
                <div class="empty-title">暂无未处理工单</div>
              </div>
            </div>
          </div>
        </div>

        <div class="p alarm-panel">
          <div class="p-hd">
            <span>{{ selectedStore ? '该粮库报警' : '异常报警' }}</span>
            <span class="alarm-badge" v-if="pendingAlarms.length">{{ pendingAlarms.length }}</span>
            <span class="meta">点击柱状图查看详情 ›</span>
          </div>
          <div class="p-bd alarm-bd">
            <div v-if="alarmTypeStats.length" ref="alarmChartEl" class="alarm-chart"></div>
            <div v-else class="empty-state empty-lg">
              <i class="empty-icon">!</i>
              <div class="empty-title">暂无待处理报警</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="m-body loading" v-else>数据加载中…</div>

    <!-- 合同详情弹窗 -->
    <el-dialog v-model="contractVisible" title="合同详情" width="560px" class="contract-dlg tech-dlg" :append-to-body="true" destroy-on-close>
      <template v-if="currentContract">
        <div class="dlg-status-bar">
          <span class="dlg-badge" :class="currentContract.status">{{ currentContract.statusText || leaseTabText(currentContract) }}</span>
          <span class="dlg-sub">合同编号：{{ currentContract.contractNo || '—' }}</span>
        </div>
        <div class="detail-grid">
          <div class="dg-cell wide"><span>合同名称</span><b>{{ currentContract.name }}</b></div>
          <div class="dg-cell"><span>所属粮库</span><b>{{ currentContract.grainStoreName }}</b></div>
          <div class="dg-cell"><span>合同金额</span><b>{{ currentContract.amount != null ? '¥' + fmt(currentContract.amount) : '—' }}</b></div>
          <div class="dg-cell"><span>甲方</span><b>{{ currentContract.partyA || '—' }}</b></div>
          <div class="dg-cell"><span>乙方</span><b>{{ currentContract.partyB || '—' }}</b></div>
          <div class="dg-cell"><span>开始日期</span><b>{{ currentContract.startDate }}</b></div>
          <div class="dg-cell"><span>结束日期</span><b>{{ currentContract.endDate }}</b></div>
          <div class="dg-cell"><span>合同状态</span><b>{{ currentContract.contractStatus }}</b></div>
          <div class="dg-cell"><span>到期情况</span><b :class="currentContract.status">{{ leaseDaysText(currentContract) }}</b></div>
          <div class="dg-cell wide" v-if="currentContract.status === 'terminated'"><span>中止原因</span><b class="reason">{{ currentContract.terminateReason || '—' }}</b></div>
        </div>
      </template>
    </el-dialog>

    <!-- 工单详情弹窗 -->
    <el-dialog v-model="woVisible" title="工单详情" width="520px" class="workorder-dlg tech-dlg" :append-to-body="true" destroy-on-close>
      <template v-if="currentWorkOrder">
        <div class="dlg-status-bar">
          <span class="dlg-badge" :class="'s' + (currentWorkOrder.status == null ? 0 : currentWorkOrder.status)">{{ currentWorkOrder.statusText }}</span>
          <span class="dlg-sub">优先级：{{ currentWorkOrder.priorityText }}</span>
        </div>
        <div class="detail-grid">
          <div class="dg-cell wide"><span>工单标题</span><b>{{ currentWorkOrder.title }}</b></div>
          <div class="dg-cell"><span>工单编号</span><b>{{ currentWorkOrder.orderNo || currentWorkOrder.id }}</b></div>
          <div class="dg-cell"><span>所属粮库</span><b>{{ currentWorkOrder.grainStoreName }}</b></div>
          <div class="dg-cell"><span>上报人</span><b>{{ currentWorkOrder.reporter || '—' }}</b></div>
          <div class="dg-cell"><span>处理人</span><b>{{ currentWorkOrder.assignee || '—' }}</b></div>
          <div class="dg-cell"><span>创建时间</span><b>{{ currentWorkOrder.createTime }}</b></div>
          <div class="dg-cell wide block"><span>工单内容</span><b class="pre-wrap">{{ currentWorkOrder.content || '—' }}</b></div>
        </div>
      </template>
    </el-dialog>

    <!-- 报警列表弹窗 -->
    <el-dialog v-model="alarmVisible" :title="selectedStore ? '该粮库报警详情' : '异常报警详情'" width="960px" class="alarm-dlg" :append-to-body="true" destroy-on-close top="14vh">
      <div class="alarm-dlg-summary">
        <div class="as-cell as-danger"><span>待处理</span><b>{{ pendingAlarms.length }}</b></div>
        <div class="as-cell as-warn"><span>处理中</span><b>{{ handlingAlarms }}</b></div>
        <div class="as-cell as-ok"><span>已处理</span><b>{{ totalHandled }}</b></div>
        <div class="as-cell as-total"><span>总计</span><b>{{ (selectedStore ? storeAlarms : alarms).length }}</b></div>
      </div>
      <el-table :data="selectedStore ? storeAlarms : alarms" stripe max-height="520" class="tech-tbl alarm-tech-tbl" :row-class-name="alarmRowClass">
        <el-table-column label="" width="50" align="center">
          <template #default="scope">
            <span class="row-dot" :class="scope.row.status === 'warn' ? 'r' : scope.row.status === 'handling' ? 'w' : 'g'"></span>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="报警内容" min-width="220" show-overflow-tooltip />
        <el-table-column label="所属粮库" width="140" show-overflow-tooltip>
          <template #default="scope">{{ scope.row.grainStoreName || '—' }}</template>
        </el-table-column>
        <el-table-column prop="type" label="类型" width="110">
          <template #default="scope">
            <el-tag size="small" effect="dark" :type="alarmTypeTag(scope.row.type)">{{ scope.row.type }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template #default="scope">
            <span class="st" :class="scope.row.status">{{ scope.row.statusText }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="time" label="报警时间" width="160" />
      </el-table>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import * as echarts from 'echarts'
import { Close } from '@element-plus/icons-vue'
import { getDashboardOverview } from '../api'
import CameraIcon from '../components/CameraIcon.vue'
import { sysConfig } from '../utils/sysConfig'

const router = useRouter()

const overview = ref(null)
const loading = ref(true)
const clock = ref('')
const today = ref('')
const weather = ref({ temp: '--', humidity: '--', rainText: '—', precip: 0, wind: '—' })
const weatherSource = ref('—')

// 选中粮库
const selectedId = ref(null)
const selectedStore = computed(() => {
  if (selectedId.value == null) return null
  return (overview.value?.stores || []).find(s => s.id === selectedId.value) || null
})

// 地图
const amapEl = ref(null)
const mapError = ref(false)
let amap = null
let amapPromise = null
// 复用 MapPicker 中已申请的高德 key（请确认有效；无效则地图显示加载失败）
const AMAP_KEY = 'ed62f646a11d1babb55dd6a94190ed00'
const AMAP_SECURITY_CODE = '625c3101178e183f7794bb6fe6aefd83'

// 地图标记引用（用于状态筛选显隐）
const mapMarkers = []
// 状态筛选：按标点颜色过滤，默认全选
const activeStatuses = ref([1, 2, 3])
const statusOptions = computed(() => {
  const counts = { 1: 0, 2: 0, 3: 0 }
  storesWithCoord.value.forEach(s => { if (counts[s.status] != null) counts[s.status]++ })
  return [
    { v: 1, label: '在用', color: '#36A2FF', count: counts[1] },
    { v: 2, label: '租赁', color: '#FFD866', count: counts[2] },
    { v: 3, label: '闲置', color: '#8FA9C6', count: counts[3] }
  ]
})
const toggleStatus = (v) => {
  const i = activeStatuses.value.indexOf(v)
  if (i >= 0) activeStatuses.value = activeStatuses.value.filter(x => x !== v)
  else activeStatuses.value = [...activeStatuses.value, v].sort()
  applyStatusFilter()
}
const applyStatusFilter = () => {
  mapMarkers.forEach(m => {
    if (activeStatuses.value.includes(m.status)) m.marker.show()
    else m.marker.hide()
  })
}

// 合同执行情况
const activeLeaseTab = ref('expired')
const contractVisible = ref(false)
const currentContract = ref(null)

// 工单详情
const woVisible = ref(false)
const currentWorkOrder = ref(null)

// 片区分布 ECharts
const areaChartEl = ref(null)
let areaChart = null

// 平面图宽高比（用于摄像机点位对齐）
const planAspect = ref(null)
// 视频弹窗
const videoVisible = ref(false)
const videoCam = ref(null)

const SVG = {
  temp: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8FA9C6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path></svg>',
  drop: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8FA9C6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path></svg>',
  rain: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8FA9C6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 13a4 4 0 0 0-8 0"></path><path d="M6 15a4.5 4.5 0 0 1 .9-8.94A6 6 0 0 1 18 8.5a3.5 3.5 0 0 1 .5 6.96"></path><line x1="8" y1="19" x2="8.5" y2="21"></line><line x1="12" y1="19" x2="12" y2="22"></line><line x1="16" y1="19" x2="15.5" y2="21"></line></svg>',
  wind: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8FA9C6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9.59 4.59A2 2 0 1 1 11 8H2"></path><path d="M12.59 19.41A2 2 0 1 0 14 16H2"></path><path d="M17.73 7.73A2.5 2.5 0 1 1 19.5 12H2"></path></svg>'
}
const iconTemp = SVG.temp, iconDrop = SVG.drop, iconRain = SVG.rain, iconWind = SVG.wind

const summary = computed(() => overview.value?.summary || {})
const storeByArea = computed(() => overview.value?.storeByArea || [])
const alarms = computed(() => overview.value?.alarms || [])
const alarmPending = computed(() => overview.value?.alarmPending || 0)
const monitors = computed(() => overview.value?.monitors || [])
const warehouses = computed(() => overview.value?.warehouses || [])
const lease = computed(() => overview.value?.lease || { total: 0, expired: 0, soon: 0, normal: 0, terminated: 0, list: [] })
const workOrders = computed(() => overview.value?.workOrders || [])
const patrolRecords = computed(() => overview.value?.patrolRecords || [])

const storesWithCoord = computed(() => (overview.value?.stores || []).filter(s => s.lng != null && s.lat != null))
const storeCameras = computed(() => selectedId.value == null ? [] : monitors.value.filter(m => m.grainStoreId === selectedId.value))
const planCameras = computed(() => storeCameras.value.filter(c => c.posX != null && c.posY != null))
const storeWarehouses = computed(() => selectedId.value == null ? [] : warehouses.value.filter(w => w.grainStoreId === selectedId.value))
const storeContracts = computed(() => {
  if (selectedId.value == null) return []
  const list = (lease.value.list || []).filter(c => c.grainStoreId === selectedId.value)
  const order = { terminated: 0, expired: 1, soon: 2, normal: 3 }
  return list.sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9))
})
const storeAlarms = computed(() => selectedId.value == null ? [] : alarms.value.filter(a => a.grainStoreId === selectedId.value))

/* ===== 巡检情况 computed ===== */
const PATROL_EXPIRE_MS = 24 * 60 * 60 * 1000
const storePatrols = computed(() => selectedId.value == null ? [] : patrolRecords.value.filter(p => p.grainStoreId === selectedId.value))
const patrolTotal = computed(() => storePatrols.value.length)
const patrolExpired = computed(() => storePatrols.value.filter(p => {
  if (Number(p.taskStatus) === 2) return false
  if (!p.createTime) return false
  return Date.now() - new Date(p.createTime).getTime() > PATROL_EXPIRE_MS
}).length)
const patrolDone = computed(() => storePatrols.value.filter(p => p.taskStatus === 2).length)
const patrolDoing = computed(() => storePatrols.value.filter(p => p.taskStatus === 1 && !isPatrolExpired(p)).length)
const patrolPending = computed(() => storePatrols.value.filter(p => p.taskStatus === 0 && !isPatrolExpired(p)).length)
function isPatrolExpired(p) {
  if (Number(p.taskStatus) === 2) return false
  if (!p.createTime) return false
  return Date.now() - new Date(p.createTime).getTime() > PATROL_EXPIRE_MS
}
const patrolRate = computed(() => patrolTotal.value > 0 ? Math.round(patrolDone.value * 100 / patrolTotal.value) : 0)
// 近7天巡检完成数（每天 executeTime 为当天的已完成数）
// 返回 [{ label: '周二', date: '9-23', value: 3 }, ...] 从早到晚
function countPatrolDoneByDay() {
  const days = getLast7DaysWithInfo()
  const map = Object.fromEntries(days.map(d => [d.date, 0]))
  storePatrols.value.forEach(p => {
    if (p.taskStatus !== 2) return
    const raw = p.executeTime || p.createTime
    if (!raw) return
    const str = String(raw)
    const m = str.match(/^(\d{1,2})-(\d{1,2})/)
    if (m) {
      const key = `${parseInt(m[1])}-${parseInt(m[2])}`
      if (map[key] != null) map[key]++
    }
  })
  return days.map(d => ({ label: d.label, isToday: d.isToday, value: map[d.date] }))
}

// 返回近7天数组，每项含 date 键 / 星期几 / 是否今天
function getLast7DaysWithInfo() {
  const weekMap = ['日', '一', '二', '三', '四', '五', '六']
  const arr = []
  const today = new Date()
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    arr.push({
      date: `${d.getMonth() + 1}-${d.getDate()}`,
      label: weekMap[d.getDay()],
      isToday: i === 0
    })
  }
  return arr
}

/* ===== P3: KPI 卡片 computed ===== */
const totalCapacity = computed(() => storeWarehouses.value.reduce((s, w) => s + (w.capacity || 0), 0))
const usedCapacity = computed(() => storeWarehouses.value.reduce((s, w) => s + (w.usedCapacity || 0), 0))
const usedRate = computed(() => totalCapacity.value > 0 ? Math.round(usedCapacity.value * 100 / totalCapacity.value) : 0)
const pendingStoreAlarms = computed(() => storeAlarms.value.filter(a => a.status === 'warn').length)
const pendingStoreWorkOrders = computed(() => storeWorkOrders.value.length)

/* ===== P0: 仓房利用率 ===== */
const whPct = (w) => {
  if (!w.capacity || w.capacity <= 0) return 0
  return Math.min(100, Math.round((w.usedCapacity || 0) * 100 / w.capacity))
}
const whPctClass = (w) => {
  const p = whPct(w)
  if (p >= 90) return 'wh-red'
  if (p >= 60) return 'wh-yellow'
  return 'wh-green'
}

/* ===== P2: 监控类型分布 ===== */
const storeCameraTypes = computed(() => {
  const map = {}
  storeCameras.value.forEach(c => {
    const t = (c.cameraType || '其他').toLowerCase()
    let label = '其他'
    if (t.includes('bullet') || t.includes('枪')) label = '枪机'
    else if (t.includes('dome') || t.includes('半球')) label = '半球'
    else if (t.includes('ptz') || t.includes('球')) label = '球机'
    else if (t.includes('box') || t.includes('箱')) label = '箱机'
    map[label] = (map[label] || 0) + 1
  })
  const total = storeCameras.value.length || 1
  const colors = { '枪机': '#409EFF', '半球': '#67C23A', '球机': '#E6A23C', '箱机': '#F56C6C', '其他': '#909399' }
  return Object.entries(map).map(([label, count]) => ({ label, count, pct: Math.round(count * 100 / total), color: colors[label] || '#909399' }))
})

// 报警：当前视图（总览 / 选中粮库）的待处理报警
const displayAlarms = computed(() => selectedStore.value ? storeAlarms.value : alarms.value)
const pendingAlarms = computed(() => displayAlarms.value.filter(a => a.status === 'warn'))
const totalHandled = computed(() => displayAlarms.value.filter(a => a.status === 'ok').length)
const handlingAlarms = computed(() => displayAlarms.value.filter(a => a.status === 'handling').length)
const latestPendingAlarms = computed(() => pendingAlarms.value.slice(0, 4))
const alarmRowClass = ({ row }) => row.status === 'warn' ? 'row-warn' : row.status === 'handling' ? 'row-handling' : ''

// 报警按类型分组统计（仅待处理）
const alarmTypeStats = computed(() => {
  const map = {}
  pendingAlarms.value.forEach(a => { map[a.type] = (map[a.type] || 0) + 1 })
  return Object.entries(map).map(([type, count]) => ({ type, count }))
})

// 弹窗 & 图表
const alarmVisible = ref(false)
const alarmChartEl = ref(null)
let alarmChart = null
const trendChartEl = ref(null)
let trendChart = null

/* ===== P1: 近7天趋势聚合 ===== */
function getLast7Days() {
  const days = []
  const today = new Date()
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    days.push(`${d.getMonth() + 1}-${d.getDate()}`)
  }
  return days
}
function countByDay(list, dateField) {
  const days = getLast7Days()
  const map = Object.fromEntries(days.map(d => [d, 0]))
  list.forEach(item => {
    const raw = item[dateField]
    if (!raw) return
    const str = String(raw)
    const m = str.match(/^(\d{1,2})-(\d{1,2})/)
    if (m) {
      const key = `${parseInt(m[1])}-${parseInt(m[2])}`
      if (map[key] != null) map[key]++
    }
  })
  return days.map(d => map[d])
}
const trend7Labels = computed(() => getLast7Days())
const trend7WorkOrders = computed(() => countByDay(storeWorkOrders.value, 'createTime'))
const trend7Alarms = computed(() => countByDay(storeAlarms.value, 'time'))
const hasTrendData = computed(() => trend7WorkOrders.value.some(v => v > 0) || trend7Alarms.value.some(v => v > 0))

const alarmTypeColor = (type) => {
  if (!type) return '#6B829C'
  if (type.includes('温度')) return '#F56C6C'
  if (type.includes('湿度')) return '#E6A23C'
  if (type.includes('设备') || type.includes('故障') || type.includes('离线')) return '#409EFF'
  if (type.includes('入侵') || type.includes('门禁')) return '#B37FEB'
  return '#6B829C'
}
const alarmTypeTag = (type) => {
  if (!type) return 'info'
  if (type.includes('温度')) return 'danger'
  if (type.includes('湿度')) return 'warning'
  if (type.includes('设备') || type.includes('离线')) return ''
  if (type.includes('入侵') || type.includes('门禁')) return 'success'
  return 'info'
}
function renderAlarmChart() {
  if (!alarmChartEl.value) return
  if (!alarmChart) {
    alarmChart = echarts.init(alarmChartEl.value)
    alarmChart.on('click', () => { alarmVisible.value = true })
  }
  const stats = alarmTypeStats.value
  if (!stats.length) { alarmChart.clear(); return }
  const colors = stats.map(s => alarmTypeColor(s.type))
  alarmChart.setOption({
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(8,16,28,.92)', borderColor: 'rgba(64,124,200,.45)',
      textStyle: { color: '#EAF2FC' },
      formatter: p => `<div style="font-weight:500;margin-bottom:4px;">${p[0].name}</div><b style="color:${p[0].color};font-size:16px;">${p[0].value}</b> 条待处理（点击查看详情）`
    },
    grid: { left: 80, right: 30, top: 8, bottom: 8, containLabel: false },
    xAxis: { type: 'value', show: false },
    yAxis: {
      type: 'category',
      data: stats.map(s => s.type),
      inverse: true,
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { color: '#BFD9F5', fontSize: 12 }
    },
    series: [{
      type: 'bar', barWidth: 16, data: stats.map(s => s.count),
      emphasis: { focus: 'series', itemStyle: { shadowBlur: 10, shadowColor: 'rgba(77,155,255,.6)' } },
      itemStyle: {
        borderRadius: [0, 5, 5, 0],
        color: (p) => new echarts.graphic.LinearGradient(0, 0, 1, 0, [
          { offset: 0, color: colors[p.dataIndex] + '40' },
          { offset: 1, color: colors[p.dataIndex] }
        ])
      },
      label: { show: true, position: 'right', color: '#EAF2FC', fontSize: 12.5, fontWeight: 700, formatter: '{c}' }
    }]
  }, true)
}

/* ===== P1: 近7天趋势图 ===== */
function renderTrendChart() {
  if (!trendChartEl.value) return
  if (trendChart) { trendChart.dispose(); trendChart = null }
  trendChart = echarts.init(trendChartEl.value)
  const labels = trend7Labels.value
  const woData = trend7WorkOrders.value
  const alData = trend7Alarms.value
  trendChart.setOption({
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(8,16,28,.92)', borderColor: 'rgba(64,124,200,.45)',
      textStyle: { color: '#EAF2FC' }, axisPointer: { type: 'cross', label: { backgroundColor: '#409EFF' } }
    },
    legend: { data: ['工单', '报警'], textStyle: { color: '#BFD9F5', fontSize: 11 }, bottom: 0, itemWidth: 12, itemHeight: 8 },
    grid: { left: 36, right: 16, top: 16, bottom: 30 },
    xAxis: {
      type: 'category', data: labels,
      axisLine: { lineStyle: { color: 'rgba(64,124,200,.3)' } },
      axisTick: { show: false },
      axisLabel: { color: '#8FA9C6', fontSize: 11 }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { color: '#8FA9C6', fontSize: 10 },
      splitLine: { lineStyle: { color: 'rgba(64,124,200,.12)' } }
    },
    series: [
      { name: '工单', type: 'line', smooth: true, symbol: 'circle', symbolSize: 6, data: woData,
        itemStyle: { color: '#409EFF' },
        lineStyle: { width: 2, color: '#409EFF' },
        areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(64,158,255,.35)' }, { offset: 1, color: 'rgba(64,158,255,.02)' }
        ])}
      },
      { name: '报警', type: 'line', smooth: true, symbol: 'circle', symbolSize: 6, data: alData,
        itemStyle: { color: '#F56C6C' },
        lineStyle: { width: 2, color: '#F56C6C' },
        areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(245,108,108,.35)' }, { offset: 1, color: 'rgba(245,108,108,.02)' }
        ])}
      }
    ]
  }, true)
}

const pendingWorkOrders = computed(() => workOrders.value.filter(w => w.pending))
const storeWorkOrders = computed(() => selectedId.value == null ? [] : pendingWorkOrders.value.filter(w => w.grainStoreId === selectedId.value))
const displayWorkOrders = computed(() => selectedStore.value ? storeWorkOrders.value : pendingWorkOrders.value)

const storeOnline = computed(() => storeCameras.value.filter(m => m.online).length)
const storeOnlinePct = computed(() => storeCameras.value.length ? Math.round(storeOnline.value * 100 / storeCameras.value.length) : 0)
const monitorOnlinePct = computed(() => {
  const s = summary.value
  if (!s.monitorTotal) return 0
  return Math.round((s.monitorOnline || 0) * 100 / s.monitorTotal)
})

const filteredLeaseList = computed(() => (lease.value.list || []).filter(c => c.status === activeLeaseTab.value))
const leaseTabText = (c) => {
  const tab = c ? c.status : activeLeaseTab.value
  return tab === 'terminated' ? '已终止' : tab === 'expired' ? '已到期' : tab === 'soon' ? '30天内到期' : '正常'
}
const leaseDaysText = (c) => {
  if (!c) return ''
  if (c.status === 'terminated') return '合同已终止'
  if (c.status === 'expired') return '已逾期 ' + Math.abs(c.days) + ' 天'
  if (c.status === 'soon') return c.days + ' 天内到期'
  return '正常'
}

// 中间底部 5 项统计：粮库总量 / 在用 / 闲置 / 租赁 / 待处理工单
const centerStatsArr = computed(() => {
  if (selectedStore.value) {
    return [
      { l: '仓房数量', v: storeWarehouses.value.length },
      { l: '监控总数', v: storeCameras.value.length, cls: 'c-b' },
      { l: '离线监控', v: storeCameras.value.length - storeOnline.value, cls: 'c-w' },
      { l: '报警条数', v: storeAlarms.value.length, cls: 'c-g' },
      { l: '在用工单', v: storeAlarms.value.filter(a => a.status === 'warn').length, cls: 'c-r' }
    ]
  }
  const c = overview.value?.centerStats || {}
  return [
    { l: '粮库总量', v: c.storeTotal },
    { l: '在用', v: c.inUse, cls: 'c-b' },
    { l: '闲置', v: c.idle, cls: 'c-w' },
    { l: '租赁', v: c.leased, cls: 'c-g' },
    { l: '待处理工单', v: c.workOrder, cls: 'c-r' }
  ]
})

const fmt = (n) => (n == null ? '0' : Number(n).toLocaleString('zh-CN'))
const fmtOneDecimal = (n) => {
  if (n == null || isNaN(n)) return '0.0'
  const v = Number(n)
  return (Math.round(v * 10) / 10).toFixed(1)
}
const isImage = (u) => !!u && /\.(png|jpe?g|gif|webp|bmp)$/i.test(u)
const statusText = (st) => st == 1 ? '在用' : st == 2 ? '租赁' : st == 3 ? '闲置' : '未知'
/* ===== P0: 仓房状态（与数据库 warehouse 表一致：0空闲 1使用中 2维护中）===== */
const whState = (w) => (w.status == 1 ? 'on' : w.status == 2 ? 'repair' : 'off')
const whStateText = (w) => {
  if (w.status == 1) return '使用中'
  if (w.status == 2) return '维护中'
  if (w.status == 0) return '空闲'
  if (w.status == null) return '空闲'
  return '未知'
}

const selectStore = (s) => {
  selectedId.value = s.id
  if (s.planImage) {
    const img = new Image()
    img.onload = () => { planAspect.value = (img.naturalWidth / img.naturalHeight) || null }
    img.src = s.planImage
  } else {
    planAspect.value = null
  }
}
const deselect = () => {
  // 先 dispose 旧的 ECharts（此时 v-if DOM 还活着）
  if (areaChart) { areaChart.dispose(); areaChart = null }
  selectedId.value = null
  // 双 nextTick：等 v-if DOM 重建完再 init + setOption
  nextTick(() => {
    nextTick(() => {
      if (areaChartEl.value) renderAreaChart()
      if (amap) amap.resize()
    })
  })
}
const openVideo = (cam) => { videoCam.value = cam; videoVisible.value = true }
const openContract = (c) => { currentContract.value = c; contractVisible.value = true }
const openWorkOrder = (w) => { currentWorkOrder.value = w; woVisible.value = true }

const goBack = () => router.push('/index')

/* ===== 片区分布 ECharts ===== */
function renderAreaChart () {
  if (!areaChartEl.value) return
  if (!areaChart) areaChart = echarts.init(areaChartEl.value)
  const list = storeByArea.value
  if (!list.length) {
    areaChart.clear()
    return
  }
  const areas = list.map(x => x.area)
  const inUse = list.map(x => x.inUse || 0)
  const leased = list.map(x => x.leased || 0)
  const idle = list.map(x => x.idle || 0)
  const labelOption = {
    show: true,
    position: 'inside',
    color: '#EAF2FC',
    fontSize: 10,
    fontWeight: 400,
    formatter: p => p.value || '',
    verticalAlign: 'middle',
    align: 'center'
  }
  areaChart.setOption({
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(8,16,28,.92)',
      borderColor: 'rgba(64,124,200,.45)',
      textStyle: { color: '#EAF2FC' },
      formatter: params => {
        const name = params[0]?.name || ''
        const fullName = name.endsWith('街道') ? name : name + '街道'
        let html = `<div style="font-weight:500;margin-bottom:6px;">${fullName}</div>`
        params.forEach(p => {
          html += `<div style="display:flex;align-items:center;gap:8px;margin:3px 0;"><span style="width:8px;height:8px;border-radius:50%;background:${p.color.colorStops ? p.color.colorStops[0].color : p.color};"></span><span style="flex:1;">${p.seriesName}</span><b>${p.value}</b></div>`
        })
        return html
      }
    },
    grid: { left: 56, right: 24, top: 6, bottom: 28 },
    legend: { data: ['在用', '租赁', '闲置'], bottom: 0, textStyle: { color: '#8FA9C6', fontSize: 11 }, itemWidth: 10, itemHeight: 10 },
    xAxis: { type: 'value', show: false },
    yAxis: {
      type: 'category',
      data: areas,
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#BFD9F5', fontSize: 12, width: 48, overflow: 'truncate', align: 'right' }
    },
    series: [
      { name: '在用', type: 'bar', stack: 'total', data: inUse, barWidth: 12, barGap: '30%', itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [{ offset: 0, color: '#2E8BFF' }, { offset: 1, color: '#4D9BFF' }]) }, label: { ...labelOption, color: '#ffffff' } },
      { name: '租赁', type: 'bar', stack: 'total', data: leased, barWidth: 12, itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [{ offset: 0, color: '#E6C24A' }, { offset: 1, color: '#FFD866' }]) }, label: { ...labelOption, color: '#3a2e08' } },
      { name: '闲置', type: 'bar', stack: 'total', data: idle, barWidth: 12, itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [{ offset: 0, color: '#6B829C' }, { offset: 1, color: '#8FA9C6' }]) }, label: { ...labelOption, color: '#ffffff' } }
    ]
  }, true)
}

/* ===== 高德地图 ===== */
function loadAMap() {
  if (window.AMap) return Promise.resolve(window.AMap)
  if (amapPromise) return amapPromise
  amapPromise = new Promise((resolve, reject) => {
    if (AMAP_SECURITY_CODE) window._AMapSecurityConfig = { securityJsCode: AMAP_SECURITY_CODE }
    const script = document.createElement('script')
    script.src = 'https://webapi.amap.com/maps?v=2.0&key=' + encodeURIComponent(AMAP_KEY)
    script.async = true
    script.onload = () => resolve(window.AMap)
    script.onerror = () => reject(new Error('AMap load failed'))
    document.head.appendChild(script)
  })
  return amapPromise
}
function storeColor(st) {
  if (st == 1) return '#36A2FF'
  if (st == 2) return '#FFD866'
  return '#8FA9C6'
}
function markerHtml(s) {
  const c = storeColor(s.status)
  return `<div class="amk" style="--c:${c}"><i></i><span>${s.name || '粮库'}</span></div>`
}
function drawJiangningBoundary(AMap) {
  return new Promise((resolve) => {
    if (!AMap.DistrictSearch) {
      AMap.plugin(['AMap.DistrictSearch'], () => {})
      resolve(null)
      return
    }
    const district = new AMap.DistrictSearch({ subdistrict: 0, extensions: 'all', level: 'district' })
    district.search('江宁区', (status, result) => {
      if (status !== 'complete' || !result || !result.districtList || !result.districtList[0]) {
        resolve(null)
        return
      }
      const bounds = result.districtList[0].boundaries || []
      if (!bounds.length) { resolve(null); return }
      const polygons = bounds.map(b => new AMap.Polygon({
        path: b,
        strokeColor: '#2E8BFF',
        strokeWeight: 2,
        strokeOpacity: 0.9,
        fillColor: '#2E8BFF',
        fillOpacity: 0.08,
        zIndex: 50
      }))
      amap.add(polygons)
      resolve(polygons)
    })
  })
}
async function initMap() {
  if (amap || !amapEl.value) return
  try {
    const AMap = await loadAMap()
    await new Promise((resolve) => AMap.plugin(['AMap.PlaceSearch', 'AMap.Geocoder', 'AMap.DistrictSearch'], resolve))
    const list = storesWithCoord.value
    const center = list.length ? [list[0].lng, list[0].lat] : [118.797, 32.06]
    amap = new AMap.Map(amapEl.value, {
      zoom: 12,
      center,
      mapStyle: 'amap://styles/darkblue' // 高德官方暗色样式
    })
    list.forEach(s => {
      const mk = new AMap.Marker({
        position: [s.lng, s.lat],
        content: markerHtml(s),
        offset: new AMap.Pixel(-46, -22),
        zIndex: 100
      })
      mk.on('click', () => selectStore(s))
      amap.add(mk)
      mapMarkers.push({ status: s.status, marker: mk })
    })
    await drawJiangningBoundary(AMap)
    if (list.length > 1) amap.setFitView(null, false, [60, 60, 60, 60])
    applyStatusFilter()
    setTimeout(() => amap && amap.resize(), 300)
  } catch (e) {
    mapError.value = true
    console.error(e)
  }
}

let clockTimer = null, refreshTimer = null
const tick = () => {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  clock.value = `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  const week = ['星期日','星期一','星期二','星期三','星期四','星期五','星期六'][d.getDay()]
  today.value = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${week}`
}

const loadDashboard = async () => {
  try {
    const res = await getDashboardOverview()
    overview.value = res.data
    if (!amap && !selectedId.value) nextTick(initMap)
    nextTick(() => { renderAreaChart(); renderAlarmChart(); renderTrendChart() })
  } catch (e) {
    console.error('大屏数据加载失败', e)
  } finally {
    loading.value = false
  }
}

const fetchWeather = async () => {
  let lat = 32.06, lng = 118.80
  const base = selectedStore.value && selectedStore.value.lat != null
    ? [selectedStore.value.lng, selectedStore.value.lat]
    : storesWithCoord.value.length
      ? [storesWithCoord.value.reduce((a, s) => a + s.lat, 0) / storesWithCoord.value.length,
         storesWithCoord.value.reduce((a, s) => a + s.lng, 0) / storesWithCoord.value.length]
      : [lng, lat]
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${base[1].toFixed(4)}&longitude=${base[0].toFixed(4)}&current=temperature_2m,relative_humidity_2m,precipitation,wind_direction_10m&timezone=Asia%2FShanghai`
  try {
    const r = await fetch(url)
    if (!r.ok) throw new Error('weather http ' + r.status)
    const j = await r.json()
    const c = j.current || {}
    weather.value = {
      temp: c.temperature_2m != null ? c.temperature_2m.toFixed(1) : '--',
      humidity: c.relative_humidity_2m != null ? c.relative_humidity_2m : '--',
      precip: c.precipitation || 0,
      rainText: (c.precipitation || 0) > 0 ? '有雨' : '晴',
      wind: degToCompass(c.wind_direction_10m)
    }
    weatherSource.value = 'Open-Meteo'
  } catch (e) {
    weatherSource.value = '离线(默认)'
  }
}
const degToCompass = (deg) => {
  if (deg == null) return '—'
  const dirs = ['北风','东北风','东风','东南风','南风','西南风','西风','西北风']
  return dirs[Math.round(deg / 45) % 8]
}

// 选中粮库变化时重新拉取天气 + 重绘图表
watch(selectedId, () => {
  if (overview.value) { fetchWeather(); nextTick(() => { renderAlarmChart(); renderTrendChart() }) }
})

// 报警无数据时释放图表实例，避免隐藏 DOM 后无法正确渲染
watch(() => alarmTypeStats.value.length, (len) => {
  if (!len && alarmChart) {
    alarmChart.dispose()
    alarmChart = null
  }
})

const handleResize = () => {
  if (areaChart) areaChart.resize()
  if (alarmChart) alarmChart.resize()
  if (trendChart) trendChart.resize()
  if (amap) amap.resize()
}

onMounted(() => {
  tick()
  clockTimer = setInterval(tick, 1000)
  loadDashboard()
  fetchWeather()
  refreshTimer = setInterval(loadDashboard, 30000)
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  clearInterval(clockTimer)
  clearInterval(refreshTimer)
  window.removeEventListener('resize', handleResize)
  if (areaChart) { areaChart.dispose(); areaChart = null }
  if (alarmChart) { alarmChart.dispose(); alarmChart = null }
  if (trendChart) { trendChart.dispose(); trendChart = null }
  if (amap) { amap.destroy(); amap = null }
})
</script>

<style scoped>
.dash-root{ position:fixed; inset:0; overflow:hidden; background:#04070E; display:flex; flex-direction:column;
  font-family:'Bahnschrift','Segoe UI','PingFang SC','Microsoft YaHei',sans-serif; }
/* ===== 方案 A 变量（来自设计稿） ===== */
.v-a, .dash-root{
  --acc:#2E8BFF; --acc-soft:rgba(46,139,255,.16); --acc-glow:rgba(46,139,255,.4);
  --slate:#2A4463; --warn:#FF9A5C; --ok:#4AEAB0; --gold:#FFD866; --danger:#FF5C5C;
  --ink:#EAF2FC; --ink2:#8FA9C6; --ink3:#566D88;
  --pl:rgba(64,124,200,.28); --hair:rgba(64,124,200,.1);
  --pb:linear-gradient(180deg,rgba(16,34,58,.72),rgba(8,18,32,.72));
  --pbg:#081523; --pt:#BFD9F5; --brr:2px;
  --title-c:#F2F7FD; --title-f:'Bahnschrift','Segoe UI','Microsoft YaHei';
  --title-w:500; --title-sh:0 0 30px rgba(46,139,255,.55);
  --hbtn:rgba(64,124,200,.4);
  background:
    radial-gradient(900px 480px at 50% -8%, rgba(46,139,255,.16), transparent 62%),
    radial-gradient(700px 420px at 4% 108%, rgba(46,139,255,.07), transparent 60%),
    radial-gradient(700px 420px at 98% 106%, rgba(74,234,176,.05), transparent 60%), #04070E;
}
.dash-root::before{ content:''; position:absolute; inset:0; pointer-events:none;
  background-image:linear-gradient(rgba(64,124,200,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(64,124,200,.05) 1px,transparent 1px);
  background-size:56px 56px; }
.m-hd{flex:0 0 74px; position:relative; display:flex; align-items:center; z-index:5;
  background:linear-gradient(180deg,rgba(13,28,48,.9),rgba(6,13,24,.4)); border-bottom:1px solid rgba(64,124,200,.35);}
.m-hd::after{ content:''; position:absolute; left:0; right:0; bottom:-1px; height:2px; background:linear-gradient(90deg,transparent 6%,#2E8BFF 50%,transparent 94%);}
.h-side{flex:1; display:flex; align-items:center; padding:0 26px;}
.h-clock{font-size:17px; letter-spacing:2px; color:var(--ink2); font-variant-numeric:tabular-nums;}
.h-clock small{font-size:12px; letter-spacing:3px; color:var(--ink3); margin-left:12px;}
.h-right{justify-content:flex-end; gap:12px;}
.h-btn{font-size:12.5px; letter-spacing:3px; color:var(--ink2); padding:7px 18px; cursor:pointer; border:1px solid var(--hbtn); background:transparent;}
.h-btn.on{color:var(--ink); background:var(--acc-soft); border-color:var(--acc); box-shadow:0 0 14px var(--acc-glow);}
.h-btn.back{border-color:var(--acc); color:#cfe2ff;}
.h-title{flex:0 0 auto; padding:0 58px; text-align:center; position:relative;
  font-size:30px; letter-spacing:9px; text-indent:9px; color:var(--title-c);
  font-family:var(--title-f); font-weight:var(--title-w); text-shadow:var(--title-sh);}
.h-title b{position:relative; z-index:2;}
/* 标题两侧渐变横线 + 菱形装饰 */
.h-title::before,.h-title::after{ content:''; position:absolute; top:50%; height:1px; width:260px; transform:translateY(-50%);
  background:linear-gradient(90deg, transparent, rgba(46,139,255,.55) 40%, rgba(46,139,255,.9) 50%, rgba(46,139,255,.55) 60%, transparent);}
.h-title::before{right:calc(100% - 24px);}
.h-title::after{left:calc(100% - 24px);}
.h-title .l,.h-title .r{position:absolute; top:50%; width:12px; height:12px; border:1px solid rgba(46,139,255,.8); transform:translateY(-50%) rotate(45deg); box-shadow:0 0 10px rgba(46,139,255,.45); background:rgba(46,139,255,.12); z-index:3;}
.h-title .l{left:18px;}
.h-title .r{right:18px;}

.m-body{flex:1; min-height:0; display:flex; gap:14px; padding:14px 16px 16px; z-index:4;}
.col{display:flex; flex-direction:column; gap:14px; min-height:0;}
.col.side{flex:0 0 400px; width:400px;}
.col.center{flex:1 1 auto; min-width:0;}

.p{border:1px solid var(--pl); background:var(--pb); border-radius:var(--brr); display:flex; flex-direction:column; min-height:0;height:230px; position:relative; box-shadow:inset 0 0 40px rgba(46,139,255,.05);}
.p.grow{flex:1 1 auto;}
.p::before,.p::after{content:''; position:absolute; width:12px; height:12px; pointer-events:none;}
.p::before{top:-1px; left:-1px; border-top:2px solid #4D9BFF; border-left:2px solid #4D9BFF;}
.p::after{bottom:-1px; right:-1px; border-bottom:2px solid #4D9BFF; border-right:2px solid #4D9BFF;}
.p-hd{flex:0 0 42px; display:flex; align-items:center; gap:10px; padding:0 16px; font-size:15px; letter-spacing:4px; color:var(--pt); position:relative;}
.p-hd::before{content:''; width:4px; height:15px; background:linear-gradient(180deg,#4D9BFF,#1B5FB8); box-shadow:0 0 10px rgba(77,155,255,.8);}
.p-hd::after{content:''; flex:1; height:1px; margin-left:4px; background:linear-gradient(90deg,rgba(64,124,200,.5),transparent);}
.p-hd .meta{margin-left:auto; font-size:11.5px; letter-spacing:2px; color:var(--ink3); font-variant-numeric:tabular-nums;}
.p-bd{flex:1; min-height:0; padding:8px 16px 14px; display:flex; flex-direction:column; gap:8px;}

.p.overview{flex:0 0 auto; height:318px;}
.p.store-kpi{flex:0 0 auto; height:318px;}
.p.area{flex:0 0 340px;}
.p.contract{flex:1 1 auto; min-height:220px;}

.kpis{display:grid; gap:6px; height:100%; align-items:center;}
.kpis > div{display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; height:100%; padding:4px 0;}
.kpis-4{grid-template-columns:repeat(2,1fr); gap:10px;}
.kpis-4 .kpi-card{background:rgba(8,16,28,.48); border:1px solid rgba(64,124,200,.2); border-radius:6px; box-shadow:inset 0 0 14px rgba(46,139,255,.05); padding:8px 0;}
.kpi-v{font-size:32px; font-weight:300; line-height:1; color:var(--ink); font-variant-numeric:tabular-nums; letter-spacing:.5px; text-shadow:0 0 18px rgba(46,139,255,.45);}
.kpi-v.area{font-size:28px;}
.kpi-v.cam{font-size:30px;}
.kpi-v .u{font-size:13px; font-weight:400; color:var(--ink3); margin-left:4px;}
.kpi-l{font-size:12px; letter-spacing:2.5px; color:var(--ink3); margin-top:8px; white-space:nowrap;}
.split{display:flex; height:9px; overflow:hidden; background:rgba(255,255,255,.05); border-radius:var(--brr);}
.split i{display:block; height:100%;}
.legend{display:flex; gap:18px; font-size:12px; color:var(--ink2); font-variant-numeric:tabular-nums; letter-spacing:.5px;}
.legend span{display:inline-flex; align-items:center; gap:6px;}
.legend span::before{content:''; width:8px; height:8px; border-radius:50%; background:currentColor;}
.c1{color:var(--acc)} .c2{color:var(--slate)} .c3{color:var(--gold)}
.c-b{color:var(--acc)} .c-w{color:var(--warn)} .c-g{color:var(--gold)} .c-r{color:var(--danger)}

/* 粮库片区分布 —— ECharts */
.area-chart{flex:1; width:100%; min-height:0;}

/* 合同执行情况 */
.contract-tabs{display:grid; grid-template-columns:repeat(4,1fr); gap:6px; margin-bottom:10px;}
.ct-item{display:flex; flex-direction:column; align-items:center; padding:7px 2px; background:rgba(8,16,28,.5); border:1px solid var(--hair); border-radius:4px; cursor:pointer; transition:all .15s;}
.ct-item.active{border-color:rgba(46,139,255,.65); background:rgba(46,139,255,.14); box-shadow:0 0 14px rgba(46,139,255,.15);}
.ct-item.term.active{border-color:rgba(255,92,92,.7); background:rgba(255,92,92,.16); box-shadow:0 0 14px rgba(255,92,92,.2);}
.ct-item b{font-size:20px; font-weight:300; color:var(--ink); font-variant-numeric:tabular-nums; line-height:1;}
.ct-item span{font-size:10.5px; color:var(--ink3); margin-top:4px; letter-spacing:.5px; text-align:center; line-height:1.2; padding:0 2px;}
.lease-list{flex:1; overflow:auto; display:flex; flex-direction:column; gap:8px; padding-right:4px;}
.lease-list::-webkit-scrollbar{width:5px; height:5px;}
.lease-list::-webkit-scrollbar-track{background:rgba(255,255,255,.04); border-radius:3px;}
.lease-list::-webkit-scrollbar-thumb{background:rgba(64,124,200,.45); border-radius:3px;}
.lease-list::-webkit-scrollbar-thumb:hover{background:rgba(64,124,200,.7);}
.lease-item{padding:9px 12px; border-left:3px solid var(--hair); background:rgba(8,16,28,.42); border-radius:0 4px 4px 0; cursor:pointer; transition:background .15s;}
.lease-item:hover{background:rgba(8,16,28,.7);}
.lease-item.expired{border-left-color:var(--danger);}
.lease-item.soon{border-left-color:var(--gold);}
.lease-item.normal{border-left-color:var(--ok);}
.lease-item.terminated{border-left-color:var(--danger);}
.lease-h{display:flex; align-items:center; justify-content:space-between; gap:8px;}
.lease-name{font-size:13px; color:var(--ink); letter-spacing:.5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
.lease-store{font-size:11px; color:var(--ink3); flex:0 0 auto;}
.lease-m{display:flex; align-items:center; justify-content:space-between; margin-top:5px; font-size:12px; color:var(--ink2);}
.lease-days{font-variant-numeric:tabular-nums;}
.lease-days.expired{color:var(--danger);}
.lease-days.soon{color:var(--gold);}
.lease-days.normal{color:var(--ok);}
.lease-days.terminated{color:var(--danger);}

/* P3: KPI 卡片 */
.store-kpi .p-bd{gap:10px;}
.sk-head{display:flex; align-items:center; justify-content:space-between;}
.sk-name{font-size:17px; color:var(--ink); letter-spacing:2px; font-weight:500;}
.sk-kpis{display:grid; grid-template-columns:repeat(4,1fr); gap:8px;}
.sk-card{
  padding:10px 8px; border-radius:5px; text-align:center; position:relative; overflow:hidden;
  background:rgba(64,124,200,.08); border:1px solid rgba(64,124,200,.18);
}
.sk-card b{display:block; font-size:19px; font-weight:700; color:#EAF2FC; font-variant-numeric:tabular-nums;}
.sk-card span{display:block; font-size:11px; color:#8FA9C6; margin-top:2px; letter-spacing:1px;}
.sk-c1{border-color:rgba(64,158,255,.35);} .sk-c1 b{color:#409EFF;}
.sk-c2{border-color:rgba(103,194,58,.35);}  .sk-c2 b{color:#67C23A;}
.sk-c3{border-color:rgba(230,162,60,.35);}  .sk-c3 b{color:#E6A23C;}
.sk-c4{border-color:rgba(143,169,198,.35);} .sk-c4 b{color:#8FA9C6;}
.sk-c5{border-color:rgba(64,158,255,.35);} .sk-c5 b{color:#409EFF;}
.sk-c6{border-color:rgba(74,234,176,.35);}  .sk-c6 b{color:#4AEAB0;}
.sk-c7{border-color:rgba(245,108,108,.4);} .sk-c7 b{color:#F56C6C;}
.sk-c8{border-color:rgba(230,162,60,.4);}  .sk-c8 b{color:#E6A23C;}
.sk-detail{display:flex; flex-wrap:wrap; gap:0; border-top:1px solid var(--hair); padding-top:8px;}
.sk-detail > div{flex:1 1 50%; padding:4px 0; font-size:12.5px; display:flex; gap:6px;}
.sk-detail span{color:var(--ink3); letter-spacing:1px; flex-shrink:0;}
.sk-detail b{color:var(--ink); font-weight:400; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;}
.sk-addr{flex:1 1 100% !important;}
.tag{display:inline-flex; align-self:flex-start; padding:2px 12px; border-radius:12px; font-size:12px; letter-spacing:2px;}
.tag.st1{color:#cfe2ff; background:rgba(46,139,255,.18); border:1px solid rgba(46,139,255,.5);}
.tag.st2{color:#ffe6a8; background:rgba(255,216,102,.14); border:1px solid rgba(255,216,102,.5);}
.tag.st3{color:#c4d2e2; background:rgba(143,169,198,.14); border:1px solid rgba(143,169,198,.5);}

/* P0: 仓房利用率进度条 */
.wh-wrap{gap:6px; overflow-y:auto; flex:1 1 auto; min-height:0; padding-right:4px;}
.wh-wrap::-webkit-scrollbar{width:5px;}
.wh-wrap::-webkit-scrollbar-thumb{background:rgba(77,155,255,.3); border-radius:3px;}
.wh-wrap::-webkit-scrollbar-track{background:transparent;}
.wh-bar{padding:6px 8px; border-radius:4px; background:rgba(64,124,200,.06); border:1px solid rgba(64,124,200,.12);}
.wh-bar-h{display:flex; align-items:center; gap:8px; margin-bottom:5px;}
.wh-n{font-size:13px; color:#EAF2FC; flex:1; letter-spacing:1px;}
.wh-s{font-size:11px; padding:1px 8px; border-radius:10px; flex-shrink:0;}
.wh-s.on{color:var(--ok); background:rgba(74,234,176,.12);}
.wh-s.repair{color:#E6A23C; background:rgba(230,162,60,.12);}
.wh-s.off{color:var(--ink3); background:rgba(143,169,198,.12);}
.wh-pct{font-size:13px; font-weight:700; font-variant-numeric:tabular-nums; flex-shrink:0;}
.wh-bar-track{height:8px; border-radius:4px; background:rgba(64,124,200,.12); overflow:hidden;}
.wh-bar-fill{height:100%; border-radius:4px; transition:width .4s ease;}
.wh-bar-fill.wh-green{background:linear-gradient(90deg,#67C23A,#4AEAB0);}
.wh-bar-fill.wh-yellow{background:linear-gradient(90deg,#E6A23C,#FFD866);}
.wh-bar-fill.wh-red{background:linear-gradient(90deg,#F56C6C,#FF8787);}
.wh-pct.wh-green{color:#67C23A;}
.wh-pct.wh-yellow{color:#E6A23C;}
.wh-pct.wh-red{color:#F56C6C;}
.wh-bar-sub{display:flex; gap:10px; margin-top:4px; font-size:11px; color:var(--ink3); font-variant-numeric:tabular-nums;}

/* 中列 */
.center{display:flex; flex-direction:column; gap:14px; min-height:0;}
.center-top{flex:0 0 auto; display:flex; align-items:center; justify-content:space-between; padding:0 6px;}
.ct-title{font-size:18px; letter-spacing:4px; color:var(--pt); display:flex; align-items:center; gap:10px; text-shadow:0 0 18px rgba(46,139,255,.4);}
.ct-title .dot{width:10px; height:10px; border-radius:50%; background:var(--acc); box-shadow:0 0 10px var(--acc);}
.back-btn{border-color:var(--acc); color:#cfe2ff; background:rgba(46,139,255,.12);}
.center-main{flex:1; min-height:0; position:relative; overflow:hidden; background:transparent; border:none; box-shadow:none;}
.amap-box{position:absolute; inset:0;}
.map-error{position:absolute; inset:0; display:flex; align-items:center; justify-content:center; padding:20px; text-align:center; color:#f56c6c; font-size:13px; background:rgba(8,13,24,.9); z-index:5;}
/* 地图右上角：状态筛选（按标点颜色） */
.status-filter{position:absolute; top:14px; right:14px; z-index:7; background:rgba(8,16,28,.82); border:1px solid var(--hair); border-radius:6px; padding:10px 12px; backdrop-filter:blur(4px); box-shadow:0 6px 22px rgba(0,0,0,.4);}
.status-filter .sf-btns{display:flex; flex-direction:column; gap:6px;}
.status-filter .sf-btn{display:flex; align-items:center; gap:8px; padding:5px 10px; border-radius:12px; cursor:pointer; border:1px solid transparent; font-size:13px; transition:all .15s;}
.status-filter .sf-btn i{width:9px; height:9px; border-radius:50%; flex:0 0 auto;}
.status-filter .sf-btn span{color:var(--ink); letter-spacing:1px;}
.status-filter .sf-btn b{margin-left:auto; font-variant-numeric:tabular-nums; color:var(--ink2); font-weight:400;}
.status-filter .sf-btn.active{border-color:rgba(64,124,200,.5); background:rgba(46,139,255,.12);}
.status-filter .sf-btn:not(.active){opacity:.45;}
/* 平面图：只保留一张图 + 摄像机标记，去掉多余背景框 */
.plan-wrap{position:absolute; inset:0; display:flex; align-items:center; justify-content:center; padding:18px; background:transparent;}
.plan-box{position:relative; max-width:100%; max-height:100%;}
.plan-img{width:100%; height:100%; display:block; object-fit:contain; }
.cam-marker{position:absolute; transform:translate(-50%,-50%); cursor:pointer; transition:transform .1s; display:flex; flex-direction:column; align-items:center; z-index:3;}
.cam-marker:hover{transform:translate(-50%,-50%) scale(1.18); z-index:5;}
.cam-tip{font-size:11px; color:var(--ink); background:rgba(8,16,28,.78); border:1px solid var(--hair); padding:1px 7px; border-radius:9px; margin-top:2px; white-space:nowrap; letter-spacing:1px;}
/* 统一空数据提示 */
.empty-state{flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; padding:16px; color:var(--ink3); font-size:13px; text-align:center;}
.empty-state .empty-icon{display:inline-flex; align-items:center; justify-content:center; width:26px; height:26px; border-radius:50%; border:1px solid var(--warn); color:var(--warn); font-size:14px; font-style:normal; line-height:1; box-shadow:0 0 8px rgba(230,162,60,.25);}
.empty-state .empty-title{letter-spacing:1.5px; line-height:1.4;}
.empty-state .empty-desc{font-size:12px; color:var(--ink3); opacity:.75; line-height:1.4;}
.empty-state.empty-lg{gap:10px; font-size:14px;}
.empty-state.empty-lg .empty-icon{width:34px; height:34px; font-size:18px;}
.empty-state.empty-lg .empty-title{font-size:15px; letter-spacing:2px;}
.empty-state.empty-lg .empty-desc{font-size:13px; opacity:.8;}

/* 高德自定义标记 */
:deep(.amk){display:flex; align-items:center; gap:7px; padding:5px 12px 5px 8px; background:rgba(8,16,28,.85); border:1px solid var(--c,#36A2FF); border-radius:16px; cursor:pointer; white-space:nowrap; box-shadow:0 0 14px rgba(46,139,255,.3); transform:translateZ(0);}
:deep(.amk i){width:10px; height:10px; border-radius:50%; background:var(--c,#36A2FF); box-shadow:0 0 8px var(--c,#36A2FF);}
:deep(.amk span){font-size:13px; color:#EAF2FC; letter-spacing:1px;}

/* 巡检情况 */
.patrol-bd{gap:8px;}
.pr-top{display:flex; align-items:center; gap:10px; flex-shrink:0;}
.pr-ring{position:relative; width:64px; height:64px; flex-shrink:0;}
.pr-ring-svg{width:100%; height:100%; transform:rotate(-90deg);}
.pr-ring-bg{fill:none; stroke:rgba(64,124,200,.15); stroke-width:3;}
.pr-ring-fg{
  fill:none; stroke:#67C23A; stroke-width:3; stroke-linecap:round;
  transition:stroke-dasharray .6s ease;
  filter:drop-shadow(0 0 4px rgba(103,194,58,.5));
}
.pr-ring-text{position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center;}
.pr-ring-text b{font-size:15px; font-weight:700; color:#67C23A; font-variant-numeric:tabular-nums; line-height:1;}
.pr-ring-text span{font-size:9px; color:#8FA9C6; margin-top:2px; letter-spacing:1px;}
.pr-stats{flex:1; display:grid; grid-template-columns:repeat(4,1fr); gap:4px;}
.pr-stat{display:flex; flex-direction:column; align-items:center; justify-content:center; padding:4px 2px; border-radius:4px; min-height:0;}
.pr-stat b{font-size:15px; font-weight:700; font-variant-numeric:tabular-nums; line-height:1.1;}
.pr-stat span{font-size:10px; color:#8FA9C6; letter-spacing:0; white-space:nowrap; margin-top:1px;}
.pr-done{background:rgba(103,194,58,.1); border:1px solid rgba(103,194,58,.3);} .pr-done b{color:#67C23A;}
.pr-doing{background:rgba(64,158,255,.1); border:1px solid rgba(64,158,255,.3);} .pr-doing b{color:#409EFF;}
.pr-pend{background:rgba(230,162,60,.1); border:1px solid rgba(230,162,60,.3);} .pr-pend b{color:#E6A23C;}
.pr-expired{background:rgba(245,108,108,.12); border:1px solid rgba(245,108,108,.35);} .pr-expired b{color:#F56C6C;}
.pr-bars{border-top:1px solid var(--hair); padding-top:6px; flex:1 1 auto; min-height:0; display:flex; flex-direction:column;}
.pr-bars-title{font-size:11px; color:#8FA9C6; letter-spacing:1px; margin-bottom:4px;}
.pr-bars-row{display:flex; align-items:flex-end; gap:6px; flex:1 1 auto; min-height:80px;}
.pr-bar-item{flex:1; display:flex; flex-direction:column; align-items:center; gap:2px; height:100%; justify-content:flex-end;}
.pr-bar-num{font-size:10px; color:#EAF2FC; font-variant-numeric:tabular-nums; font-weight:500; margin-bottom:1px;}
.pr-bar-zero{color:rgba(143,169,198,.6);}
.pr-bar-v{
  width:100%; max-width:18px; border-radius:3px 3px 0 0;
  background:linear-gradient(180deg,#409EFF,#67C23A);
  box-shadow:0 0 6px rgba(64,158,255,.4);
  transition:height .4s ease; min-height:4px;
}
.pr-bar-l{font-size:10px; color:#8FA9C6;}
.pr-bar-today{color:#67C23A; font-weight:700;}

/* 全局 overview 模式：监控类型分布（总览时也保留） */
.cam-types{display:flex; flex-direction:column; gap:4px; padding-top:4px;}
.ct-divider{height:1px; background:linear-gradient(90deg,transparent,rgba(64,124,200,.3),transparent); margin:4px 0;}
.ct-row{display:flex; align-items:center; gap:8px; font-size:12px;}
.ct-name{color:#BFD9F5; flex-shrink:0; width:44px;}
.ct-bar{flex:1; height:6px; border-radius:3px; background:rgba(64,124,200,.12); overflow:hidden;}
.ct-bar i{display:block; height:100%; background:linear-gradient(90deg,#409EFF,#67C23A); border-radius:3px; transition:width .3s;}
.ct-v{color:#8FA9C6; font-variant-numeric:tabular-nums; flex-shrink:0; width:20px; text-align:right;}

/* P1: 中心底部 trend / stats 切换 */
.stats-wrap{flex:0 0 auto; display:flex;}
.stats-wrap .stats{flex:1;}
.trend-wrap{
  flex:1; border:1px solid var(--pl); background:var(--pb); border-radius:var(--brr);
  padding:8px 14px; display:flex; flex-direction:column; min-height:120px;
}
.trend-title{font-size:12px; letter-spacing:2px; color:var(--pt); font-weight:500; margin-bottom:2px;}
.trend-chart{flex:1; min-height:96px;}

.stats{flex:0 0 auto; display:grid; grid-template-columns:repeat(5,1fr); border:1px solid var(--pl); background:var(--pb); border-radius:var(--brr);}
.stats > div{padding:10px 14px; border-right:1px solid var(--hair); display:flex; flex-direction:column; align-items:center; text-align:center;}
.stats > div:last-child{border-right:0;}
.stats .v{font-size:28px; font-weight:300; line-height:1; color:var(--ink); font-variant-numeric:tabular-nums; text-shadow:0 0 18px rgba(46,139,255,.4);}
.stats .l{font-size:11px; letter-spacing:2px; color:var(--ink3); margin-top:4px;}

/* 右列 */
.wx{display:grid; grid-template-columns:1fr 1fr; flex:1;}
.wx > div{display:flex; align-items:center; gap:12px; padding:10px 6px; border-bottom:1px solid var(--hair);}
.wx > div:nth-child(odd){border-right:1px solid var(--hair);}
.wx > div:nth-last-child(-n+2){border-bottom:0;}
.wx svg{stroke:var(--ink3); flex:0 0 auto; color:var(--ink2);}
.wx .t{display:flex; flex-direction:column;}
.wx .l{font-size:11.5px; letter-spacing:2.5px; color:var(--ink3);}
.wx .v{font-size:22px; font-weight:300; color:var(--ink); font-variant-numeric:tabular-nums; line-height:1.25;}
.wx .v .u{font-size:12px; color:var(--ink3);}
.wx .s{font-size:11.5px; color:var(--ink3);}

/* 未处理工单 */
.wo-list{flex:1; overflow:auto; display:flex; flex-direction:column; gap:8px; padding-right:4px;}
.wo-list::-webkit-scrollbar{width:5px; height:5px;}
.wo-list::-webkit-scrollbar-track{background:rgba(255,255,255,.04); border-radius:3px;}
.wo-list::-webkit-scrollbar-thumb{background:rgba(64,124,200,.45); border-radius:3px;}
.wo-list::-webkit-scrollbar-thumb:hover{background:rgba(64,124,200,.7);}
.wo-item{padding:10px 12px; background:rgba(8,16,28,.42); border:1px solid var(--hair); border-radius:4px; cursor:pointer; transition:all .15s;}
.wo-item:hover{background:rgba(8,16,28,.7); border-color:rgba(64,124,200,.35);}
.wo-h{display:flex; align-items:center; gap:8px; margin-bottom:5px;}
.wo-no{font-size:12px; color:var(--ink3); font-variant-numeric:tabular-nums;}
.wo-pri{font-size:11px; padding:1px 7px; border-radius:8px;}
.wo-pri.p1{color:#ffd4d4; background:rgba(255,92,92,.16); border:1px solid rgba(255,92,92,.4);}
.wo-pri.p2{color:#ffe6a8; background:rgba(255,154,92,.14); border:1px solid rgba(255,154,92,.4);}
.wo-pri.p3{color:#cfe2ff; background:rgba(46,139,255,.14); border:1px solid rgba(46,139,255,.4);}
.wo-pri.p4{color:#c4d2e2; background:rgba(143,169,198,.12); border:1px solid rgba(143,169,198,.4);}
.wo-st{font-size:11px; padding:1px 7px; border-radius:8px; margin-left:auto;}
.wo-st.s0{color:#ffd4bc; background:rgba(255,154,92,.14); border:1px solid rgba(255,154,92,.4);}
.wo-st.s1{color:#baf5dc; background:rgba(74,234,176,.14); border:1px solid rgba(74,234,176,.45);}
.wo-st.s2{color:#c4d2e2; background:rgba(143,169,198,.12); border:1px solid rgba(143,169,198,.4);}
.wo-title{font-size:13px; color:var(--ink); letter-spacing:.5px; margin-bottom:5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
.wo-m{display:flex; align-items:center; gap:12px; font-size:11px; color:var(--ink3); font-variant-numeric:tabular-nums;}
.wo-m span{white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}

/* ===== 异常报警面板 ===== */
.alarm-panel .alarm-badge{
  background:#F56C6C; color:#fff; font-size:11px; font-weight:700; line-height:16px;
  padding:0 6px; border-radius:10px; margin-left:6px;
  animation:alarm-pulse 1.6s ease-in-out infinite;
}
@keyframes alarm-pulse{0%,100%{opacity:1} 50%{opacity:.55}}
.alarm-panel{min-height:180px;}
.alarm-bd{align-items:stretch;}
.alarm-chart{flex:1; min-height:160px; cursor:pointer;}
.alarm-chart.empty{display:flex; align-items:center; justify-content:center;}
/* ===== 报警弹窗 summary ===== */
.alarm-dlg-summary{display:flex; gap:12px; padding:16px 18px 14px;}
.alarm-dlg-summary .as-cell{flex:1; padding:14px 16px; border-radius:6px;
  display:flex; flex-direction:column; gap:6px; position:relative; overflow:hidden;}
.alarm-dlg-summary .as-cell::before{content:''; position:absolute; left:0; top:0; bottom:0; width:3px;}
.alarm-dlg-summary .as-cell span{font-size:11px; color:#8FA9C6; letter-spacing:2px; text-transform:uppercase;}
.alarm-dlg-summary .as-cell b{font-size:22px; color:#EAF2FC; font-weight:700; font-variant-numeric:tabular-nums;}
.alarm-dlg-summary .as-danger{background:rgba(245,108,108,.1); border:1px solid rgba(245,108,108,.3);}
.alarm-dlg-summary .as-danger::before{background:#F56C6C; box-shadow:0 0 10px #F56C6C;}
.alarm-dlg-summary .as-danger b{color:#F56C6C;}
.alarm-dlg-summary .as-warn{background:rgba(230,162,60,.1); border:1px solid rgba(230,162,60,.3);}
.alarm-dlg-summary .as-warn::before{background:#E6A23C; box-shadow:0 0 10px #E6A23C;}
.alarm-dlg-summary .as-warn b{color:#E6A23C;}
.alarm-dlg-summary .as-ok{background:rgba(74,234,176,.1); border:1px solid rgba(74,234,176,.3);}
.alarm-dlg-summary .as-ok::before{background:#4AEAB0; box-shadow:0 0 10px #4AEAB0;}
.alarm-dlg-summary .as-ok b{color:#4AEAB0;}
.alarm-dlg-summary .as-total{background:rgba(64,158,255,.1); border:1px solid rgba(64,158,255,.3);}
.alarm-dlg-summary .as-total::before{background:#409EFF; box-shadow:0 0 10px #409EFF;}
.alarm-dlg-summary .as-total b{color:#409EFF;}

/* ===== 报警弹窗 table 内组件样式 ===== */
.row-dot{width:8px; height:8px; border-radius:50%; display:inline-block;}
.row-dot.r{background:#F56C6C; box-shadow:0 0 6px #F56C6C;}
.row-dot.w{background:#E6A23C; box-shadow:0 0 6px #E6A23C;}
.row-dot.g{background:#4AEAB0; box-shadow:0 0 6px #4AEAB0;}

.tbl{width:100%; border-collapse:collapse; font-size:13px;}
.tbl th{font-weight:400; font-size:11.5px; letter-spacing:2px; color:var(--ink3); text-align:left; padding:6px 6px; border-bottom:1px solid var(--pl);}
.tbl td{padding:8.5px 6px; border-bottom:1px solid var(--hair); color:var(--ink2); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
.tbl tr:last-child td{border-bottom:0;}
.tbl .num{font-variant-numeric:tabular-nums;}
.st{display:inline-flex; align-items:center; gap:6px; font-size:12px;}
.st::before{content:''; width:6px; height:6px; border-radius:50%; background:currentColor; box-shadow:0 0 6px currentColor;}
.st.warn{color:var(--warn)} .st.ok{color:var(--ok)}

.loading{display:flex; align-items:center; justify-content:center; color:var(--ink3); font-size:18px; letter-spacing:4px;}

/* 视频弹窗 — 覆盖中间 center-main 面板 */
.video-overlay {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}
.video-overlay-inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0a1424;
  border: 1px solid rgba(64,124,200,.45);
  border-radius: 4px;
  box-shadow: 0 0 40px rgba(46,139,255,.25), inset 0 0 40px rgba(46,139,255,.05);
  overflow: hidden;
  margin: 0;
}
.video-overlay-title {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(64,124,200,.25);
  color: #EAF2FC;
  font-size: 15px;
  letter-spacing: 2px;
  font-weight: 400;
  background: #0a1424;
}
.video-overlay-title :deep(.el-button.close-btn){
  color: #7FB0E8;
  background: rgba(30, 60, 100, 0.5);
  border: 1px solid rgba(64, 124, 200, 0.4);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.video-overlay-title :deep(.el-button.close-btn:hover){
  color: #fff;
  background: rgba(46, 139, 255, 0.3);
  border-color: rgba(46, 139, 255, 0.7);
  box-shadow: 0 0 10px rgba(46, 139, 255, 0.5);
}
.video-box{width:100%; min-height:320px; background:#000; display:flex; align-items:center; justify-content:center; border:1px solid rgba(64,124,200,.25);}
.video-box.big{
  flex: 1;
  min-height: 0;
  margin: 14px 18px;
  border-radius: 2px;
}
.v-img{width:100%; height:100%; object-fit:contain; display:block;}
.v-vid{width:100%; height:100%; background:#000; object-fit:contain;}
.video-meta{flex: 0 0 auto; margin: 0 18px 14px; display:flex; align-items:center; gap:14px; font-size:13px; color:var(--ink2); letter-spacing:1px;}
.vm-tag{display:inline-flex; align-items:center; padding:2px 10px; border-radius:10px; font-size:12px;}
.vm-tag.on{color:#baf5dc; background:rgba(74,234,176,.14); border:1px solid rgba(74,234,176,.45);}
.vm-tag.off{color:#ffd4bc; background:rgba(255,154,92,.14); border:1px solid rgba(255,154,92,.45);}
.vm-store{color:var(--ink3); margin-left:auto;}
.panel-fade-enter-active,
.panel-fade-leave-active { transition: opacity .2s ease; }
.panel-fade-enter-from,
.panel-fade-leave-to { opacity: 0; }

/* 合同/工单详情弹窗 */
:deep(.contract-dlg), :deep(.workorder-dlg){background:#0a1424;}
:deep(.contract-dlg.el-dialog), :deep(.workorder-dlg.el-dialog){background:#0a1424 !important; border:1px solid rgba(64,124,200,.45) !important; border-radius:4px !important; box-shadow:0 0 40px rgba(46,139,255,.25), inset 0 0 40px rgba(46,139,255,.05);}
:deep(.contract-dlg .el-dialog__header), :deep(.workorder-dlg .el-dialog__header){margin-right:0; padding:14px 18px; border-bottom:1px solid rgba(64,124,200,.25); background:#0a1424 !important;}
:deep(.contract-dlg .el-dialog__title), :deep(.workorder-dlg .el-dialog__title){color:#EAF2FC !important; font-size:15px; letter-spacing:2px; font-weight:400;}
:deep(.contract-dlg .el-dialog__headerbtn .el-dialog__close), :deep(.workorder-dlg .el-dialog__headerbtn .el-dialog__close){color:var(--ink2) !important;}
:deep(.contract-dlg .el-dialog__headerbtn:hover .el-dialog__close), :deep(.workorder-dlg .el-dialog__headerbtn:hover .el-dialog__close){color:#fff !important;}
:deep(.contract-dlg .el-dialog__body), :deep(.workorder-dlg .el-dialog__body){padding:0; background:#0a1424 !important;}

/* 弹窗公共：状态条 + 详情网格 */
.dlg-status-bar{display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 22px; background:rgba(46,139,255,.08); border-bottom:1px solid rgba(64,124,200,.18);}
.dlg-badge{display:inline-flex; align-items:center; padding:3px 12px; border-radius:10px; font-size:12px; letter-spacing:1px;}
.dlg-badge.expired{color:#ffd4d4; background:rgba(255,92,92,.14); border:1px solid rgba(255,92,92,.4);}
.dlg-badge.soon{color:#ffe6a8; background:rgba(255,216,102,.14); border:1px solid rgba(255,216,102,.4);}
.dlg-badge.normal{color:#baf5dc; background:rgba(74,234,176,.14); border:1px solid rgba(74,234,176,.45);}
.dlg-badge.terminated{color:#ffd4d4; background:rgba(255,92,92,.16); border:1px solid rgba(255,92,92,.5);}
.dlg-badge.s0{color:#ffd4bc; background:rgba(255,154,92,.14); border:1px solid rgba(255,154,92,.4);}
.dlg-badge.s1{color:#baf5dc; background:rgba(74,234,176,.14); border:1px solid rgba(74,234,176,.45);}
.dlg-badge.s2{color:#c4d2e2; background:rgba(143,169,198,.14); border:1px solid rgba(143,169,198,.4);}
.dlg-sub{font-size:12px; color:var(--ink2); letter-spacing:1px; font-variant-numeric:tabular-nums;}
.detail-grid{display:grid; grid-template-columns:1fr 1fr; gap:1px; background:rgba(64,124,200,.12); padding:1px;}
.dg-cell{display:flex; flex-direction:column; gap:5px; padding:12px 16px; background:#0a1424;}
.dg-cell.wide{grid-column:1 / -1;}
.dg-cell.block{grid-column:1 / -1;}
.dg-cell span{font-size:11.5px; color:var(--ink3); letter-spacing:1.5px;}
.dg-cell b{font-size:13px; color:var(--ink); font-weight:400; word-break:break-all; line-height:1.5;}
.dg-cell b.pre-wrap{white-space:pre-wrap; line-height:1.7; color:#cfe2ff;}
.dg-cell b.expired{color:var(--danger);}
.dg-cell b.soon{color:var(--gold);}
.dg-cell b.normal{color:var(--ok);}
.dg-cell b.reason{color:#ffd4d4; white-space:pre-wrap; line-height:1.7;}
.dg-cell b.p1{color:#ffd4d4;}
.dg-cell b.p2{color:#ffe6a8;}
.dg-cell b.p3{color:#cfe2ff;}
.dg-cell b.p4{color:#c4d2e2;}
.dg-cell b.s0{color:var(--warn);}
.dg-cell b.s1{color:var(--ok);}
.dg-cell b.s2{color:var(--ink2);}
</style>

<style>
/* 全局覆盖 Element Plus 弹窗：确保所有大屏弹窗都是深色科技风 */
.video-dlg.el-dialog,
.contract-dlg.el-dialog,
.workorder-dlg.el-dialog,
.alarm-dlg.el-dialog {
  background-color: #0a1424 !important;
  border: 1px solid rgba(64,124,200,.45) !important;
  border-radius: 4px !important;
  box-shadow: 0 0 40px rgba(46,139,255,.25), inset 0 0 40px rgba(46,139,255,.05) !important;
}
.video-dlg .el-dialog__header,
.contract-dlg .el-dialog__header,
.workorder-dlg .el-dialog__header,
.alarm-dlg .el-dialog__header {
  margin-right: 0 !important;
  padding: 14px 18px !important;
  border-bottom: 1px solid rgba(64,124,200,.25) !important;
  background-color: #0a1424 !important;
}
.video-dlg .el-dialog__title,
.contract-dlg .el-dialog__title,
.workorder-dlg .el-dialog__title,
.alarm-dlg .el-dialog__title {
  color: #EAF2FC !important;
  font-size: 15px !important;
  letter-spacing: 2px !important;
  font-weight: 400 !important;
}
.video-dlg .el-dialog__headerbtn .el-dialog__close,
.contract-dlg .el-dialog__headerbtn .el-dialog__close,
.workorder-dlg .el-dialog__headerbtn .el-dialog__close,
.alarm-dlg .el-dialog__headerbtn .el-dialog__close {
  color: #8FA9C6 !important;
}
.video-dlg .el-dialog__headerbtn:hover .el-dialog__close,
.contract-dlg .el-dialog__headerbtn:hover .el-dialog__close,
.workorder-dlg .el-dialog__headerbtn:hover .el-dialog__close,
.alarm-dlg .el-dialog__headerbtn:hover .el-dialog__close {
  color: #fff !important;
}
.video-dlg .el-dialog__body,
.contract-dlg .el-dialog__body,
.workorder-dlg .el-dialog__body,
.alarm-dlg .el-dialog__body {
  background-color: #0a1424 !important;
  color: #EAF2FC !important;
  padding-top: 0 !important;
}

/* ===== 报警弹窗内的 el-table 深色主题 ===== */
.alarm-dlg .el-table {
  background: transparent !important;
  border: none !important;
  --el-table-tr-bg-color: transparent;
  --el-table-header-bg-color: rgba(64,124,200,.18);
  --el-table-row-hover-bg-color: rgba(64,124,200,.1);
  --el-table-border-color: rgba(64,124,200,.15);
  --el-table-header-text-color: #EAF2FC;
  --el-table-text-color: #BFD9F5;
  --el-table-text-color-regular: #EAF2FC;
  font-size: 13px;
}
.alarm-dlg .el-table::before { display: none !important; }
.alarm-dlg .el-table th.el-table__cell {
  background: rgba(64,124,200,.18) !important;
  border-bottom: 1px solid rgba(64,124,200,.3) !important;
  color: #EAF2FC !important;
  font-size: 12px !important;
  letter-spacing: 1.5px;
  font-weight: 500;
}
.alarm-dlg .el-table td.el-table__cell {
  background: transparent !important;
  border-bottom: 1px solid rgba(64,124,200,.12) !important;
  color: #EAF2FC !important;
}
.alarm-dlg .el-table .row-warn td.el-table__cell { background: rgba(245,108,108,.06) !important; }
.alarm-dlg .el-table .row-handling td.el-table__cell { background: rgba(230,162,60,.06) !important; }
.alarm-dlg .el-table__body tr:hover > td.el-table__cell {
  background: rgba(64,124,200,.12) !important;
}
.alarm-dlg .el-table__empty-block { background: transparent !important; }
.alarm-dlg .el-table__empty-text { color: #8FA9C6 !important; }

/* 覆盖 Element Plus 默认 header 背景，防止白色边框/标题区域 */
.el-dialog__header {
  background-color: transparent !important;
}
</style>
